// ToneDrillLesson.js
import React, {useEffect, useState} from 'react';

import AudioPlayer from './AudioPlayer';
import AudioRecorder from './ToneDrillAudioRecorder';
import ToneDrillPitchPlot from './ToneDrillPitchPlot';

const ToneDrillLesson = ({selectedLesson, onBack}) => {
  const [selectedLessonData, setSelectedLessonData] = useState(null);
  const [initialData, setInitialData] = useState({
    1 : {time : [], pitch : []},
    2 : {time : [], pitch : []},
    3 : {time : [], pitch : []},
    4 : {time : [], pitch : []}
  });
  const [recordedData, setRecordedData] = useState({
    1 : {time : [], pitch : []},
    2 : {time : [], pitch : []},
    3 : {time : [], pitch : []},
    4 : {time : [], pitch : []}
  });
  const [words, setWord] = useState({
    1 : {ch : '', pinyin : '', en : '', tone : '1', path : ''},
    2 : {ch : '', pinyin : '', en : '', tone : '2', path : ''},
    3 : {ch : '', pinyin : '', en : '', tone : '3', path : ''},
    4 : {ch : '', pinyin : '', en : '', tone : '4', path : ''}
  });
  const [filenames, setFilenames] = useState({1 : '', 2 : '', 3 : '', 4 : ''});
  const [index, setIndex] = useState(0);
  const [highlightedTone, setHighlightedTone] =
      useState(null); // State for highlighting
  const [recordedAudioURLs, setRecordedAudioURLs] =
      useState({1 : null, 2 : null, 3 : null, 4 : null});
  const [isRecordedAudioEnabled, setIsRecordedAudioEnabled] =
      useState({1 : false, 2 : false, 3 : false, 4 : false});
  const [gender, setGender] = useState('male'); // State for gender selection

  useEffect(() => {
    if (selectedLesson) {
      fetch(`/static/${selectedLesson.path}`)
          .then((response) => response.json())
          .then((data) => {
            if (data) {
              setSelectedLessonData(data);
            } else {
              console.error('could not fetch lesson data');
            }
          })
          .catch((error) =>
                     console.error('error fetching lesson data:', error));
    }
  }, [ selectedLesson ]);

  useEffect(() => {
    if (selectedLessonData && selectedLessonData.length > 0 &&
        index < selectedLessonData.length) {
      const lessonData = selectedLessonData[index];
      const wordData = {};

      // Iterate through the lesson data and populate wordData for
      // each tone
      lessonData.forEach(item => {
        const tone = item.tone;
        wordData[tone] = {
          ch : item.ch,
          pinyin : item.pinyin,
          en : item.en,
          tone : tone,
          path : item.path
        };
      });

      // Ensure all tones are included, even if some tones are missing
      // in the selectedLessonData
      setWord({
        1 : wordData['1'] ||
            {ch : '', pinyin : '', en : '', tone : '1', path : ''},
        2 : wordData['2'] ||
            {ch : '', pinyin : '', en : '', tone : '2', path : ''},
        3 : wordData['3'] ||
            {ch : '', pinyin : '', en : '', tone : '3', path : ''},
        4 : wordData['4'] || { ch: '', pinyin: '', en: '', tone: '4', path: '' }
      });
    } else {
      console.error('could not set words');
    }
  }, [ selectedLessonData, index ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (Object.values(words).every(w => w.ch && w.path)) {
          const promises = Object.values(words).map(async (w) => {
            const response =
                await fetch(`/static/${w.path}/${gender}/${w.ch}.json`);
            const data = await response.json();

            if (data && data.pitch) {
              const normalizedTime = data.time.map(t => t - data.time[0]);
              return {tone : w.tone, time : normalizedTime, pitch : data.pitch};
            } else {
              throw new Error(`No pitch data found for ${w.ch}`);
            }
          });

          const results = await Promise.all(promises);

          // Process results
          const initialData = results.reduce((acc, result) => {
            acc[result.tone] = {time : result.time, pitch : result.pitch};
            return acc;
          }, {});

          setInitialData(initialData);

          // Generate filenames
          const filenames = results.reduce((acc, result) => {
            acc[result.tone] = `${gender}/${
                Object.values(words).find(w => w.tone === result.tone).ch}`;
            return acc;
          }, {});

          setFilenames(filenames);
        }
      } catch (error) {
        console.error('Error fetching pitch contour data:', error);
      }
    };

    fetchData();
  }, [ words, gender ]);

  const handlePlayAudio = (tone) => {
    setHighlightedTone(tone); // Set the highlighted tone
    const audio = new Audio(`/static/pitch-shift/${filenames[tone]}.mp3`);
    audio.play();
  };

  const handleGenderChange = (event) => {
    setGender(
        event.target.value); // Update gender based on selected radio button
  };

  const handleNext = () => {
    if (selectedLessonData) {
      if (index < selectedLessonData.length - 1) {
        setIndex((prevIndex) => prevIndex + 1);
        setRecordedData({
          1 : {time : [], pitch : []},
          2 : {time : [], pitch : []},
          3 : {time : [], pitch : []},
          4 : {time : [], pitch : []}
        });
        setIsRecordedAudioEnabled({1 : false, 2 : false, 3 : false, 4 : false});
        setHighlightedTone(null); // Set the highlighted tone
      } else {
        setIndex(0);
      }
    }
  };

  const handleRecordingComplete = async (data, tone) => {
    // Set recorded data for the specific tone
    if (data && data.pitch_contour) {
      const normalizedTime =
          data.pitch_contour.time.map(t => t - data.pitch_contour.time[0]);
      setRecordedData(prevRecordedData => ({
                        ...prevRecordedData,
                        [tone] : {
                          time : normalizedTime,
                          pitch : data.pitch_contour.pitch,
                        },
                      }));
    }
  };

  const handleBack = () => {
    onBack();
    setRecordedData({
      1 : {time : [], pitch : []},
      2 : {time : [], pitch : []},
      3 : {time : [], pitch : []},
      4 : {time : [], pitch : []}
    });
  };

  const WordDisplay =
            ({words}) => {
              const tones = [ 1, 2, 3, 4 ];

  return (
    <div style={styles.container}>
      {tones.map((tone, idx) => (
        <div key={tone} style={styles.wordContainer}>
          <p><span style={styles.words}>{words[tone]?.ch || ''}</span></p>
          <p><span style={styles.pinyin}>{words[tone]?.pinyin || ''}</span></p>
          {/* <p><span style={styles.en}>{words[tone]?.en || ''}</span></p> */}
          <button onClick={() => handlePlayAudio(tone)}>Play Audio</button>
          <AudioRecorder
            tone={tone}
            onRecordingComplete={handleRecordingComplete}
            recordedAudioURL={recordedAudioURLs[tone]}
            setRecordedAudioURL={(url) => setRecordedAudioURLs(prev => ({ ...prev, [tone]: url }))}
            isRecordedAudioEnabled={isRecordedAudioEnabled[tone]}
            setIsRecordedAudioEnabled={(enabled) => setIsRecordedAudioEnabled(prev => ({ ...prev, [tone]: enabled }))}
          />
          {/* <button onClick={() => handlePlayRecorded(tone)}>Play Recorded</button> */}
        </div>
      ))}
    </div>
  );
};

  return (<div style = {styles.mainContent}><h1>Pitch Shift<
          /h1>
      {/* Radio Buttons for Gender Selection */}
      <div style={styles.genderSelector}>
        <label>
          <input
            type="radio"
            value="male"
            checked={gender === 'male'}
            onChange={handleGenderChange}
          />
          Male
        </label>
        <label>
          <input
            type="radio"
            value="female"
            checked={gender === 'female'}
            onChange={handleGenderChange}
          />
          Female
        </label>
      </div>

      {initialData && (
        <div style={styles.plotContainer}>
          <ToneDrillPitchPlot
            initialData={initialData}
            recordedData={recordedData}
        highlightedTone={
      highlightedTone} // Pass highlighted tone to the plot
          />
          </div>
      )}

      <WordDisplay words={
      words} />

                <div style = {styles.buttonGroup}>
                <button onClick = {handleBack}>Back to Lessons<
                    /button>
                <button onClick={handleNext}>Next</button>
                </div>

    </div>);
};

const styles = {
container: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  wordContainer: {
    flex: 1,
    margin: '0 10px',
    textAlign: 'center'
  },
  words: {
    fontSize: '5em',
  },
  pinyin : {
    fontSize : '3em',
  },
  en: {
    fontSize: '14px',
    color: 'darkgray',
  },
  mainContent : {
    display : 'flex',
    flexDirection : 'column',
    alignItems : 'center',
    width : '100%',
  },
  plotContainer : {
    width : '100%',
    maxWidth : '1200px',
    marginBottom : '20px',
  },
  pinyin : {
    fontSize : '3em',
  },
  audioPlayer : {
    marginBottom : '20px',
  },
  buttonGroup : {
    display : 'flex',
    justifyContent : 'space-between',
    width : '300px',
    marginTop : '20px',
  },
  toneCombinationContainer : {
    display : 'flex',
    justifyContent : 'space-between', // Align groups horizontally
            gap: '20px', // Add some space between groups
                marginTop: '20px',
            },
        toneGroup: {
          display: 'flex',
          flexDirection:
              'column', // Stack the checkboxes vertically within each group
          alignItems: 'flex-start',
        },
        checkboxLabel: {
          marginBottom: '5px', // Add some space between checkboxes
        },
};

export default ToneDrillLesson;
